import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { ThemeProvider } from "@/components/theme-provider";
import { ErrorBoundary } from "@/components/error-boundary";
import { AuthProvider, useAuth } from "@/lib/auth-context";
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

// Lazy load components
const LoginPage = lazy(() => import("./pages/LoginPage"));
const ForgotPasswordPage = lazy(() => import("./pages/login/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/login/ResetPasswordPage"));
const AuthenticatedLayout = lazy(() => import("./layouts/AuthenticatedLayout"));

function AppContent() {
  const { state } = useAuth();

  // Show loading spinner while checking auth
  if (state.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    }>
      <Routes>
        {/* Public routes */}
        <Route 
          path="/login" 
          element={state.isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />} 
        />
        <Route path="/login/forgot-password" element={<ForgotPasswordPage />} />
        {/* <Route path="/login/reset-password" element={<ResetPasswordPage />} /> */}

        {/* Protected routes */}
        <Route 
          path="/*" 
          element={state.isAuthenticated ? <AuthenticatedLayout /> : <Navigate to="/login" replace />} 
        />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="font-sans antialiased overflow-x-hidden w-full max-w-full">
        <ErrorBoundary>
          <ThemeProvider
            attribute="class"
            defaultTheme="light"
            enableSystem
            disableTransitionOnChange
          >
            <BrowserRouter>
              <AuthProvider>
                <AppContent />
                <Toaster />
              </AuthProvider>
            </BrowserRouter>
          </ThemeProvider>
        </ErrorBoundary>
      </div>
    </HelmetProvider>
  );
}

export default App;
